import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, X, Eye, Edit, Trash2, ClipboardList, ChevronDown,
  ArrowRight, Package, ChevronLeft, Save, DollarSign, Calendar,
  Globe, Clock, CheckCircle, AlertCircle, FileText, Layers,
  TrendingUp, Filter, ShieldCheck, ShieldX, ShieldQuestion, Ban,
  Container, Settings, List, BarChart3, User, MapPin,
} from 'lucide-react';
import { customerOrderService } from '../services/customerOrderService';
import { api } from '../config/api';

// ─── Status ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  validacion_finanzas: {
    label: 'Validación Finanzas',
    bg: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: Clock,
  },
  validacion_proveedores: {
    label: 'Validación Proveedores',
    bg: 'bg-purple-100 text-purple-800 border-purple-300',
    icon: ShieldQuestion,
  },
  observado: {
    label: 'Observado',
    bg: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: AlertCircle,
  },
  aprobado: {
    label: 'Aprobado',
    bg: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    icon: CheckCircle,
  },
};

// ─── Container specs ────────────────────────────────────────────────────────
export const CONTAINER_SPECS = {
  '20ft': { label: "20'",    cbm: 33, weight_min: 18, weight_max: 21 },
  '40ft': { label: "40'",    cbm: 67, weight_min: 22, weight_max: 26 },
  '40hc': { label: "40' HC", cbm: 76, weight_min: 22, weight_max: 26 },
};

const CONTAINER_OPTIONS = [
  { value: '', label: '— Sin definir —' },
  { value: '20ft', label: "20'  —  33 m³  /  18–21 t" },
  { value: '40ft', label: "40'  —  67 m³  /  22–26 t" },
  { value: '40hc', label: "40' HC  —  76 m³  /  22–26 t" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtN = (v, d = 2) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '-';
  return n.toLocaleString('es-AR', { minimumFractionDigits: d, maximumFractionDigits: d });
};

const fmtDate = (v) => {
  if (!v) return '-';
  try { return new Date(v + 'T00:00:00').toLocaleDateString('es-AR'); } catch { return v; }
};

const estimateContainers = (containerType, items, specs = CONTAINER_SPECS) => {
  if (!containerType || !items || !items.length) return null;
  const spec = specs[containerType];
  if (!spec) return null;
  const totalCbm = items.reduce((sum, it) => sum + (parseFloat(it.total_cbm) || 0), 0);
  const totalKg  = items.reduce((sum, it) => sum + (parseFloat(it.total_gross_weight) || 0), 0);
  if (!totalCbm && !totalKg) return null;
  const byVolume = totalCbm > 0 ? Math.ceil(totalCbm / spec.cbm) : 0;
  const byWeight = totalKg  > 0 ? Math.ceil(totalKg  / (spec.weight_max * 1000)) : 0;
  return { count: Math.max(byVolume, byWeight), byVolume, byWeight };
};

// ─── Initial states ─────────────────────────────────────────────────────────
const INITIAL_ORDER = {
  status: 'validacion_finanzas',
  client: '',
  client_pi: '',
  order_date: '',
  shipping_date: '',
  destination_country: '',
  loading_port: '',
  buyer: '',
  description: '',
  payment_terms: '',
  rebate: '',
  rate_discount: '',
  inspection_required: false,
  requires_en71: false,
  container_type: '',
  comments: '',
  currency: 'USD',
};

const INITIAL_ITEM = {
  product: '',
  product_name: '',
  item_code: '',
  product_variant: '',
  brand_name: '',
  category_name: '',
  customer_item_code: '',
  product_image: '',
  packaging_type_name: '',
  factory: '',
  agent: '',
  quantity: '',
  price_fob_unit: '',
  price_fob_unit_revised: '',
  cost_fob_unit: '',
  pcs_per_ctn: '',
  inners: '',
  custom_pcs_per_ctn: '',
  cbm_per_ctn: '',
  custom_cbm_per_ctn: '',
  description: '',
  customer_battery_info: '',
  observations: '',
  assortment: '',
  artwork: '',
};

// ─── Editor Tabs ────────────────────────────────────────────────────────────
const EDITOR_TABS = [
  { id: 'orden',   label: 'Información de la orden' },
  { id: 'cliente', label: 'Información del cliente' },
  { id: 'totales', label: 'Totales del pedido' },
  { id: 'detalle', label: 'Detalle del pedido' },
];

// ── Componentes de formulario (fuera del editor para evitar remounting) ──────
const Field = ({ label, children, span2 = false, labelCls = '' }) => (
  <div className={span2 ? 'sm:col-span-2' : ''}>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const OrderInput = ({ value = '', onChange, type = 'text', placeholder = '', step, readOnly = false, inputCls = '' }) => (
  <input
    type={type}
    step={step}
    className={`${inputCls}${readOnly ? ' opacity-60 cursor-default' : ''}`}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    readOnly={readOnly}
  />
);

// ══════════════════════════════════════════════════════════════════════════════
// ORDER EDITOR COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const OrderEditor = ({
  order,          // null → new order
  contacts = [],
  ports = [],
  products = [],
  onBack,
  onSaved,
  theme = 'light',
}) => {
  const isDark = theme === 'dark';
  const clientContacts = useMemo(() => contacts.filter(c => c.contactType === 'CLIENT'), [contacts]);

  const [activeTab, setActiveTab]     = useState('orden');
  const [orderData, setOrderData]     = useState(order || INITIAL_ORDER);
  const [items, setItems]             = useState([]);
  const [saving, setSaving]           = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [containerSpecs, setContainerSpecs] = useState(CONTAINER_SPECS);

  // Item edit state
  const [editingItem, setEditingItem] = useState(null); // null → add, object → edit
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm]       = useState({ ...INITIAL_ITEM });
  const [savingItem, setSavingItem]   = useState(false);
  const [deleteItem, setDeleteItem]   = useState(null);

  // Product search within item modal
  const [productSearch, setProductSearch]     = useState('');
  const [productResults, setProductResults]   = useState([]);
  const [searchingProduct, setSearchingProduct] = useState(false);

  const isNew = !order;

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark ? 'bg-[#202124] border-[#3c4043] text-[#e8eaed] placeholder-[#9aa0a6]'
           : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelCls = `block text-xs font-medium mb-1 ${isDark ? 'text-[#bdc1c6]' : 'text-gray-600'}`;
  const sText    = isDark ? 'text-[#bdc1c6]' : 'text-gray-600';
  const pText    = isDark ? 'text-[#e8eaed]' : 'text-gray-900';
  const cardCls  = `rounded-xl border shadow-sm ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`;

  // Load container specs from API (fallback to hardcoded if fails)
  useEffect(() => {
    customerOrderService.getContainerSpecs()
      .then(data => { if (data && typeof data === 'object') setContainerSpecs(data); })
      .catch(() => {});
  }, []);

  // Load items if editing existing order
  useEffect(() => {
    if (!order?.id) return;
    setLoadingItems(true);
    customerOrderService.getItems(order.id)
      .then(data => setItems(Array.isArray(data) ? data : data?.results || []))
      .catch(console.error)
      .finally(() => setLoadingItems(false));
  }, [order?.id]);

  const set = useCallback((field, value) => {
    setOrderData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Totals computed from items
  const totals = useMemo(() => {
    const total_pieces        = items.reduce((s, it) => s + (parseInt(it.quantity) || 0), 0);
    const total_cartons       = items.reduce((s, it) => s + (parseInt(it.total_cartons) || 0), 0);
    const total_cbm           = items.reduce((s, it) => s + (parseFloat(it.total_cbm) || 0), 0);
    const total_gross_weight  = items.reduce((s, it) => s + (parseFloat(it.total_gross_weight) || 0), 0);
    const total_fob_cost      = items.reduce((s, it) => s + (parseFloat(it.total_fob_cost) || 0), 0);
    const total_fob_price     = items.reduce((s, it) => s + (parseFloat(it.total_fob_price) || 0), 0);
    const margin = total_fob_cost > 0 ? ((total_fob_price / total_fob_cost - 1) * 100) : 0;
    const containers = estimateContainers(orderData.container_type, items, containerSpecs);
    return { total_pieces, total_cartons, total_cbm, total_gross_weight, total_fob_cost, total_fob_price, margin, containers };
  }, [items, orderData.container_type, containerSpecs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...orderData };
      ['rebate', 'rate_discount'].forEach(k => {
        payload[k] = payload[k] === '' ? null : (parseFloat(payload[k]) || null);
      });
      if (!payload.order_date)    payload.order_date = null;
      if (!payload.shipping_date) payload.shipping_date = null;
      if (!payload.container_type) payload.container_type = null;
      if (!payload.loading_port)  payload.loading_port = null;
      if (payload.client) payload.client = parseInt(payload.client, 10) || null;

      let saved;
      if (isNew) {
        saved = await customerOrderService.create(payload);
      } else {
        saved = await customerOrderService.update(order.id, payload);
      }
      if (onSaved) onSaved(saved);
    } catch (err) {
      console.error('Error guardando pedido:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Product search ────────────────────────────────────────────────────────
  const searchProductRef = useRef(null);
  useEffect(() => {
    if (!productSearch || productSearch.length < 2) { setProductResults([]); return; }
    setSearchingProduct(true);
    clearTimeout(searchProductRef.current);
    searchProductRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/products/?search=${encodeURIComponent(productSearch)}&limit=15`);
        const list = Array.isArray(res) ? res : res?.results || [];
        setProductResults(list);
      } catch { setProductResults([]); }
      finally { setSearchingProduct(false); }
    }, 350);
  }, [productSearch]);

  const applyProduct = (p) => {
    setItemForm(prev => ({
      ...prev,
      product: p.id,
      product_name: p.name || '',
      item_code: p.product_code || p.code || '',
      brand_name: p.brand_name || (p.brand?.name) || '',
      category_name: p.category_name || (p.category?.name) || '',
      product_variant: p.product_variant || '',
      packaging_type_name: p.packaging_type_name || (p.packaging_type?.name) || '',
      product_image: p.main_image || p.product_image || '',
      price_fob_unit: p.price_fob || '',
      cost_fob_unit: p.cost_fob || '',
      pcs_per_ctn: p.pcs_per_ctn || '',
      inners: p.inner_ctn || '',
      cbm_per_ctn: p.cbm_per_ctn || '',
      description: p.description || '',
    }));
    setProductSearch('');
    setProductResults([]);
  };

  // ── Item CRUD ─────────────────────────────────────────────────────────────
  const openAddItem = () => {
    setEditingItem(null);
    setItemForm({ ...INITIAL_ITEM });
    setProductSearch('');
    setProductResults([]);
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      product: item.product || '',
      product_name: item.product_name || '',
      item_code: item.item_code || '',
      product_variant: item.product_variant || '',
      brand_name: item.brand_name || '',
      category_name: item.category_name || '',
      customer_item_code: item.customer_item_code || '',
      product_image: item.product_image || '',
      packaging_type_name: item.packaging_type_name || '',
      factory: item.factory || '',
      agent: item.agent || '',
      quantity: item.quantity ?? '',
      price_fob_unit: item.price_fob_unit ?? '',
      price_fob_unit_revised: item.price_fob_unit_revised ?? '',
      cost_fob_unit: item.cost_fob_unit ?? '',
      pcs_per_ctn: item.pcs_per_ctn ?? '',
      inners: item.inners ?? '',
      custom_pcs_per_ctn: item.custom_pcs_per_ctn ?? '',
      cbm_per_ctn: item.cbm_per_ctn ?? '',
      custom_cbm_per_ctn: item.custom_cbm_per_ctn ?? '',
      description: item.description || '',
      customer_battery_info: item.customer_battery_info || '',
      observations: item.observations || '',
      assortment: item.assortment || '',
      artwork: item.artwork || '',
    });
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!order?.id) return;
    setSavingItem(true);
    try {
      const p = { ...itemForm };
      ['quantity', 'pcs_per_ctn', 'inners', 'custom_pcs_per_ctn'].forEach(k => {
        p[k] = p[k] === '' ? null : (parseInt(p[k], 10) || null);
      });
      ['price_fob_unit', 'price_fob_unit_revised', 'cost_fob_unit',
       'cbm_per_ctn', 'custom_cbm_per_ctn'].forEach(k => {
        p[k] = p[k] === '' ? null : (parseFloat(p[k]) || null);
      });
      if (!p.product) p.product = null;
      if (!p.factory) p.factory = null;
      if (!p.agent)   p.agent   = null;

      if (editingItem) {
        await customerOrderService.updateItem(order.id, editingItem.id, p);
      } else {
        await customerOrderService.createItem(order.id, p);
      }
      setShowItemModal(false);
      const data = await customerOrderService.getItems(order.id);
      setItems(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Error guardando ítem:', err);
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!order?.id) return;
    try {
      await customerOrderService.deleteItem(order.id, itemId);
      setItems(prev => prev.filter(it => it.id !== itemId));
    } catch (err) { console.error('Error eliminando ítem:', err); }
    setDeleteItem(null);
  };

  const setItem = (field, value) => setItemForm(prev => ({ ...prev, [field]: value }));

  // ── TAB: Información de la orden ─────────────────────────────────────────
  const TabOrden = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Estado del pedido *" labelCls={labelCls}>
        <select className={inputCls} value={orderData.status} onChange={e => set('status', e.target.value)}>
          {Object.entries(STATUS_CONFIG).map(([v, cfg]) => (
            <option key={v} value={v}>{cfg.label}</option>
          ))}
        </select>
      </Field>
      <Field label="PI del Cliente" labelCls={labelCls}>
        <OrderInput value={orderData.client_pi ?? ''} onChange={e => set('client_pi', e.target.value)} placeholder="Ej: PI-2025-001" inputCls={inputCls} />
      </Field>
      <Field label="Fecha de pedido *" labelCls={labelCls}>
        <OrderInput value={orderData.order_date ?? ''} onChange={e => set('order_date', e.target.value)} type="date" inputCls={inputCls} />
      </Field>
      <Field label="Fecha de envío *" labelCls={labelCls}>
        <OrderInput value={orderData.shipping_date ?? ''} onChange={e => set('shipping_date', e.target.value)} type="date" inputCls={inputCls} />
      </Field>
      <Field label="País de destino *" labelCls={labelCls}>
        <OrderInput value={orderData.destination_country ?? ''} onChange={e => set('destination_country', e.target.value)} placeholder="Ej: Argentina" inputCls={inputCls} />
      </Field>
      <Field label="Puerto de embarque *" labelCls={labelCls}>
        <select className={inputCls} value={orderData.loading_port || ''} onChange={e => set('loading_port', e.target.value)}>
          <option value="">— Sin definir —</option>
          {ports.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="Comerciante *" labelCls={labelCls}>
        <OrderInput value={orderData.buyer ?? ''} onChange={e => set('buyer', e.target.value)} placeholder="Nombre del comprador/comerciante" inputCls={inputCls} />
      </Field>
      <Field label="Descripción" span2 labelCls={labelCls}>
        <textarea
          rows={3} className={inputCls}
          value={orderData.description || ''}
          onChange={e => set('description', e.target.value)}
          placeholder="Descripción del pedido..."
        />
      </Field>
    </div>
  );

  // ── TAB: Información del cliente ─────────────────────────────────────────
  const TabCliente = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Cliente *" labelCls={labelCls}>
        <select className={inputCls} value={orderData.client || ''} onChange={e => set('client', e.target.value)}>
          <option value="">Seleccionar cliente...</option>
          {clientContacts.map(c => (
            <option key={c.id} value={c.id}>
              {c.codigoContacto ? `${c.codigoContacto} — ` : ''}{c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Términos de pago *" labelCls={labelCls}>
        <OrderInput value={orderData.payment_terms ?? ''} onChange={e => set('payment_terms', e.target.value)} placeholder="Ej: 30% anticipo, 70% contra BL" inputCls={inputCls} />
      </Field>
      <Field label="Rebaja (%)" labelCls={labelCls}>
        <OrderInput value={orderData.rebate ?? ''} onChange={e => set('rebate', e.target.value)} type="number" step="0.01" placeholder="0.00" inputCls={inputCls} />
      </Field>
      <Field label="Otros comentarios" labelCls={labelCls}>
        <OrderInput value={orderData.comments ?? ''} onChange={e => set('comments', e.target.value)} placeholder="Comentarios adicionales" inputCls={inputCls} />
      </Field>
      <Field label="Descuento de tarifa (%)" labelCls={labelCls}>
        <OrderInput value={orderData.rate_discount ?? ''} onChange={e => set('rate_discount', e.target.value)} type="number" step="0.01" placeholder="0.00" inputCls={inputCls} />
      </Field>
      <Field label="Tipo de contenedor *" labelCls={labelCls}>
        <select className={inputCls} value={orderData.container_type || ''} onChange={e => set('container_type', e.target.value)}>
          {CONTAINER_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </Field>
      <Field label="Cantidad estimada de contenedores" labelCls={labelCls}>
        <div className={`${inputCls} opacity-70 cursor-default font-semibold`}>
          {totals.containers != null ? totals.containers : '—'}
          {orderData.container_type && totals.containers != null && (
            <span className="text-xs font-normal ml-2 text-gray-500">
              {containerSpecs[orderData.container_type]?.label} · {fmtN(totals.total_cbm, 2)} m³ / {containerSpecs[orderData.container_type]?.cbm} m³
              {totals.total_gross_weight > 0 && ` · ${fmtN(totals.total_gross_weight / 1000, 2)} t`}
              {' '}— <span className="font-semibold text-blue-600">{totals.containers.count} {totals.containers.count === 1 ? 'contenedor' : 'contenedores'}</span>
            </span>
          )}
        </div>
      </Field>
      <div className="sm:col-span-2 flex flex-wrap gap-6 pt-1">
        {[
          { field: 'inspection_required', label: 'Inspección solicitada' },
          { field: 'requires_en71',       label: 'Requiere EN71' },
        ].map(({ field, label }) => (
          <label key={field} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={!!orderData[field]}
              onChange={e => set(field, e.target.checked)}
            />
            <span className={`text-sm ${pText}`}>{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  // ── TAB: Totales del pedido ───────────────────────────────────────────────
  const TabTotales = () => {
    const spec       = containerSpecs[orderData.container_type];
    const cbm        = totals.total_cbm;
    const kgTotal    = totals.total_gross_weight;
    const weightMaxKg = spec ? spec.weight_max * 1000 : 0;

    // Volume indicator
    const cbmPct  = spec ? Math.min((cbm  / spec.cbm)      * 100, 100) : 0;
    const cbmOver = spec && cbm > spec.cbm;
    const cbmBar  = cbmOver ? 'bg-red-500' : cbmPct > 85 ? 'bg-amber-500' : 'bg-green-500';

    // Weight indicator
    const kgPct  = weightMaxKg ? Math.min((kgTotal / weightMaxKg) * 100, 100) : 0;
    const kgOver = weightMaxKg && kgTotal > weightMaxKg;
    const kgBar  = kgOver ? 'bg-red-500' : kgPct > 85 ? 'bg-amber-500' : 'bg-blue-500';

    const containers = totals.containers;

    return (
      <div className="space-y-6 max-w-xl">
        <div className={`${cardCls} p-5 space-y-4`}>
          {[
            { label: 'Total piezas del pedido',           value: fmtN(totals.total_pieces, 0),  bold: true },
            { label: 'Total cartones del pedido',          value: fmtN(totals.total_cartons, 0), bold: true },
            { label: 'Total CBM del pedido',               value: `${fmtN(totals.total_cbm, 3)} m³`, bold: true },
            { label: 'Peso bruto total del pedido',        value: `${fmtN(kgTotal / 1000, 3)} t  (${fmtN(kgTotal, 0)} kg)`, bold: true },
            { label: 'Total Costo FOB del pedido',         value: `USD ${fmtN(totals.total_fob_cost)}` },
            { label: 'Total Precio final FOB del pedido',  value: `USD ${fmtN(totals.total_fob_price)}`, bold: true },
            { label: 'Margen del pedido',                  value: `${fmtN(totals.margin)} %` },
          ].map(({ label, value, bold }) => (
            <div key={label} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
              <span className={`text-sm ${sText}`}>{label}</span>
              <span className={`text-sm ${bold ? 'font-bold' : 'font-medium'} ${pText}`}>{value}</span>
            </div>
          ))}
        </div>

        {spec && (
          <div className={`${cardCls} p-5 space-y-4`}>
            <p className={`text-sm font-semibold ${pText}`}>
              Ocupación del contenedor — {spec.label}
              {containers && (
                <span className="ml-2 text-blue-600 font-bold text-base">{containers.count} {containers.count === 1 ? 'contenedor' : 'contenedores'}</span>
              )}
            </p>

            {/* Volumen */}
            <div>
              <div className="flex items-center justify-between mb-1 text-xs">
                <span className={sText}>📦 Volumen: {fmtN(cbm, 2)} m³ / {spec.cbm} m³</span>
                <span className={`font-bold ${cbmOver ? 'text-red-600' : cbmPct > 85 ? 'text-amber-600' : 'text-green-600'}`}>{cbmPct.toFixed(1)}%</span>
              </div>
              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${cbmBar}`} style={{ width: `${cbmPct}%` }} />
              </div>
              {cbmOver && <p className="mt-1 text-xs text-red-600 font-medium">⚠ Excede capacidad en {fmtN(cbm - spec.cbm, 2)} m³</p>}
            </div>

            {/* Peso */}
            <div>
              <div className="flex items-center justify-between mb-1 text-xs">
                <span className={sText}>⚖️ Peso: {fmtN(kgTotal / 1000, 2)} t / {spec.weight_max} t máx.</span>
                <span className={`font-bold ${kgOver ? 'text-red-600' : kgPct > 85 ? 'text-amber-600' : 'text-blue-600'}`}>{kgPct.toFixed(1)}%</span>
              </div>
              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${kgBar}`} style={{ width: `${kgPct}%` }} />
              </div>
              {kgOver && <p className="mt-1 text-xs text-red-600 font-medium">⚠ Excede límite de peso en {fmtN((kgTotal - weightMaxKg) / 1000, 2)} t</p>}
              {!kgTotal && <p className="mt-1 text-xs text-gray-400">Sin datos de peso: completá el peso bruto por caja en el catálogo.</p>}
            </div>

            {containers && (
              <div className={`text-xs rounded-lg px-3 py-2 ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <span className={sText}>Limitante: </span>
                <span className={`font-semibold ${pText}`}>
                  {containers.byWeight > containers.byVolume ? '⚖️ Peso' : '📦 Volumen'}
                </span>
                <span className={`ml-2 ${sText}`}>({containers.byVolume} por CBM, {containers.byWeight} por peso)</span>
              </div>
            )}

            <p className={`text-xs ${sText}`}>Rango de peso: {spec.weight_min}–{spec.weight_max} t</p>
          </div>
        )}

        {!order?.id && (
          <p className={`text-xs ${sText}`}>Los totales se calculan automáticamente desde los ítems del pedido.</p>
        )}
      </div>
    );
  };

  // ── TAB: Detalle del pedido ───────────────────────────────────────────────
  const TabDetalle = () => {
    const isExisting = !!order?.id;

    const cols = [
      { key: 'product_image',          label: 'Imagen',              w: 'w-16'  },
      { key: 'factory_name',           label: 'Fábrica',             w: 'w-28'  },
      { key: 'agent_name',             label: 'Proveedor',           w: 'w-28'  },
      { key: 'brand_name',             label: 'Marca',               w: 'w-24'  },
      { key: 'category_name',          label: 'Categoría',           w: 'w-28'  },
      { key: 'item_code',              label: 'Cód. Artículo',       w: 'w-28'  },
      { key: 'product_variant',        label: 'Variante',            w: 'w-28'  },
      { key: 'product_name',           label: 'Nombre',              w: 'w-48'  },
      { key: 'customer_item_code',     label: 'Cód. Cliente',        w: 'w-28'  },
      { key: 'packaging_type_name',    label: 'Tipo Embalaje',       w: 'w-28'  },
      { key: 'price_fob_unit',         label: 'Precio FOB Unit.',    w: 'w-28'  },
      { key: 'price_fob_unit_revised', label: 'Precio FOB Rev.',     w: 'w-28'  },
      { key: 'cost_fob_unit',          label: 'Costo FOB Unit.',     w: 'w-28'  },
      { key: 'quantity',               label: 'Piezas pedido',       w: 'w-24'  },
      { key: 'pcs_per_ctn',            label: 'PCS/CTN',             w: 'w-20'  },
      { key: 'inners',                 label: 'Inners',              w: 'w-20'  },
      { key: 'custom_pcs_per_ctn',     label: 'Custom PCS/CTN',      w: 'w-28'  },
      { key: 'total_cartons',          label: 'Total Cartones',      w: 'w-28'  },
      { key: 'cbm_per_ctn',            label: 'CBM/CTN',             w: 'w-24'  },
      { key: 'custom_cbm_per_ctn',     label: 'Custom CBM/CTN',      w: 'w-28'  },
      { key: 'total_cbm',              label: 'CBM Total',           w: 'w-24'  },
      { key: 'total_rmb_cost',         label: 'Costo RMB Total',     w: 'w-28'  },
      { key: 'total_fob_cost',         label: 'Costo FOB Total',     w: 'w-28'  },
      { key: 'total_fob_price',        label: 'Precio FOB Total',    w: 'w-28'  },
      { key: 'margin',                 label: 'Margen',              w: 'w-20'  },
      { key: 'customer_battery_info',  label: 'Info Batería Cliente', w: 'w-36' },
      { key: 'observations',           label: 'Observaciones',       w: 'w-40'  },
      { key: 'assortment',             label: 'Surtido de Colores',  w: 'w-36'  },
      { key: 'artwork',                label: 'Packaging Artwork',   w: 'w-36'  },
      { key: 'description',            label: 'Descripción',         w: 'w-44'  },
    ];

    const renderCell = (item, key) => {
      if (key === 'product_image') {
        return item.product_image
          ? <img src={item.product_image} alt="" className="w-10 h-10 object-cover rounded-md" />
          : <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center"><Package size={14} className="text-gray-400" /></div>;
      }
      const numKeys = ['price_fob_unit','price_fob_unit_revised','cost_fob_unit',
                       'total_fob_cost','total_fob_price','total_rmb_cost',
                       'cbm_per_ctn','custom_cbm_per_ctn','total_cbm'];
      const intKeys = ['quantity','pcs_per_ctn','inners','custom_pcs_per_ctn','total_cartons'];
      const pctKeys = ['margin'];
      const v = item[key];
      if (v == null || v === '') return <span className="text-gray-400">—</span>;
      if (pctKeys.includes(key)) return `${fmtN(v)}%`;
      if (numKeys.includes(key)) return fmtN(v);
      if (intKeys.includes(key)) return fmtN(v, 0);
      return String(v);
    };

    if (!isExisting) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package size={48} className="mb-3 text-gray-300" />
          <p className="text-sm text-gray-500 max-w-xs">
            Guardá primero el pedido para poder agregar ítems al detalle.
          </p>
        </div>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className={`text-sm ${sText}`}>{items.length} ítem{items.length !== 1 ? 's' : ''}</p>
          <button
            onClick={openAddItem}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus size={15} /> Agregar Ítem
          </button>
        </div>

        {loadingItems ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 border border-dashed border-gray-200 rounded-xl">
            <Package size={40} className="mb-3 text-gray-300" />
            <p className="text-sm text-gray-500 mb-3">No hay ítems en este pedido</p>
            <button onClick={openAddItem} className="text-sm text-blue-600 hover:underline font-medium">Agregar el primer ítem</button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {cols.map(c => (
                    <th key={c.key} className={`px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap ${c.w}`}>
                      {c.label}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, idx) => (
                  <tr key={item.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    {cols.map(c => (
                      <td key={c.key} className={`px-3 py-2.5 ${c.key === 'product_name' ? 'font-medium' : ''} text-gray-700 whitespace-nowrap`}>
                        {renderCell(item, c.key)}
                      </td>
                    ))}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEditItem(item)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Editar">
                          <Edit size={13} className="text-blue-500" />
                        </button>
                        <button onClick={() => setDeleteItem(item)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Eliminar">
                          <Trash2 size={13} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Item Modal ────────────────────────────────────────────────────────────
  const ItemModal = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className={`w-full max-w-2xl rounded-xl shadow-2xl border max-h-[90vh] flex flex-col ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`}>
        <div className={`flex items-center justify-between p-5 border-b ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-semibold ${pText}`}>{editingItem ? 'Editar Ítem' : 'Agregar Ítem'}</h3>
          <button onClick={() => setShowItemModal(false)} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={20} className={sText} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Product search */}
          <div>
            <label className={labelCls}>Buscar producto del catálogo</label>
            <div className="relative">
              <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${sText}`} />
              <input
                className={`${inputCls} pl-8`}
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                placeholder="Buscar por nombre o código..."
              />
              {searchingProduct && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
            </div>
            {productResults.length > 0 && (
              <div className="mt-1 border border-gray-200 rounded-lg shadow-lg bg-white max-h-40 overflow-y-auto">
                {productResults.map(p => (
                  <button
                    key={p.id}
                    onClick={() => applyProduct(p)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex items-center gap-2 border-b border-gray-100 last:border-0"
                  >
                    {p.main_image && <img src={p.main_image} alt="" className="w-8 h-8 object-cover rounded" />}
                    <div>
                      <div className="font-medium text-gray-800">{p.name}</div>
                      <div className="text-xs text-gray-500">{p.product_code || p.code || ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Row 1: nombre + cod artículo */}
            <div>
              <label className={labelCls}>Nombre del producto (S/A)</label>
              <input className={inputCls} value={itemForm.product_name} onChange={e => setItem('product_name', e.target.value)} placeholder="Nombre" />
            </div>
            <div>
              <label className={labelCls}>Código del Artículo (S)</label>
              <input className={inputCls} value={itemForm.item_code} onChange={e => setItem('item_code', e.target.value)} placeholder="Código" />
            </div>
            <div>
              <label className={labelCls}>Variante del Producto (S)</label>
              <input className={inputCls} value={itemForm.product_variant} onChange={e => setItem('product_variant', e.target.value)} placeholder="Ej: Rojo/Azul" />
            </div>
            <div>
              <label className={labelCls}>Código del Artículo del Cliente (A)</label>
              <input className={inputCls} value={itemForm.customer_item_code} onChange={e => setItem('customer_item_code', e.target.value)} placeholder="Cód. cliente" />
            </div>
            <div>
              <label className={labelCls}>Marca (S)</label>
              <input className={inputCls} value={itemForm.brand_name} onChange={e => setItem('brand_name', e.target.value)} placeholder="Marca" />
            </div>
            <div>
              <label className={labelCls}>Categoría (S)</label>
              <input className={inputCls} value={itemForm.category_name} onChange={e => setItem('category_name', e.target.value)} placeholder="Categoría" />
            </div>
            <div>
              <label className={labelCls}>Tipo de embalaje (S)</label>
              <input className={inputCls} value={itemForm.packaging_type_name} onChange={e => setItem('packaging_type_name', e.target.value)} placeholder="Tipo embalaje" />
            </div>
            <div>
              <label className={labelCls}>Piezas del pedido (x)</label>
              <input type="number" className={inputCls} value={itemForm.quantity} onChange={e => setItem('quantity', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className={labelCls}>Precio FOB unitario (A)</label>
              <input type="number" step="0.01" className={inputCls} value={itemForm.price_fob_unit} onChange={e => setItem('price_fob_unit', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className={labelCls}>Precio FOB unitario revisado (A)</label>
              <input type="number" step="0.01" className={inputCls} value={itemForm.price_fob_unit_revised} onChange={e => setItem('price_fob_unit_revised', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className={labelCls}>Costo FOB unitario (S)</label>
              <input type="number" step="0.01" className={inputCls} value={itemForm.cost_fob_unit} onChange={e => setItem('cost_fob_unit', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className={labelCls}>PCS/CTN (S)</label>
              <input type="number" className={inputCls} value={itemForm.pcs_per_ctn} onChange={e => setItem('pcs_per_ctn', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className={labelCls}>Inners (S)</label>
              <input type="number" className={inputCls} value={itemForm.inners} onChange={e => setItem('inners', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className={labelCls}>Custom PCS/CTN (A)</label>
              <input type="number" className={inputCls} value={itemForm.custom_pcs_per_ctn} onChange={e => setItem('custom_pcs_per_ctn', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className={labelCls}>CBM/CTN (S)</label>
              <input type="number" step="0.001" className={inputCls} value={itemForm.cbm_per_ctn} onChange={e => setItem('cbm_per_ctn', e.target.value)} placeholder="0.000" />
            </div>
            <div>
              <label className={labelCls}>Custom CBM/CTN (A)</label>
              <input type="number" step="0.001" className={inputCls} value={itemForm.custom_cbm_per_ctn} onChange={e => setItem('custom_cbm_per_ctn', e.target.value)} placeholder="0.000" />
            </div>
            <div>
              <label className={labelCls}>Información de la Batería del Cliente (A)</label>
              <input className={inputCls} value={itemForm.customer_battery_info} onChange={e => setItem('customer_battery_info', e.target.value)} placeholder="Info batería" />
            </div>
            <div>
              <label className={labelCls}>Surtido de Colores (A)</label>
              <input className={inputCls} value={itemForm.assortment} onChange={e => setItem('assortment', e.target.value)} placeholder="Surtido" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Observaciones (A)</label>
              <textarea rows={2} className={inputCls} value={itemForm.observations} onChange={e => setItem('observations', e.target.value)} placeholder="Observaciones del ítem..." />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Packaging Artwork (A)</label>
              <textarea rows={2} className={inputCls} value={itemForm.artwork} onChange={e => setItem('artwork', e.target.value)} placeholder="Instrucciones de arte/packaging..." />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Descripción (S/A)</label>
              <textarea rows={2} className={inputCls} value={itemForm.description} onChange={e => setItem('description', e.target.value)} placeholder="Descripción del producto..." />
            </div>
          </div>
        </div>

        <div className={`flex justify-end gap-3 p-5 border-t ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
          <button onClick={() => setShowItemModal(false)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">
            Cancelar
          </button>
          <button onClick={handleSaveItem} disabled={savingItem} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
            <Save size={15} />
            {savingItem ? 'Guardando...' : 'Guardar Ítem'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <>
    <div className={`${cardCls}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={18} className={sText} />
          </button>
          <div>
            <h2 className={`text-lg font-bold ${pText}`}>
              {isNew ? 'Nuevo Pedido' : orderData.order_code || 'Pedido'}
            </h2>
            {!isNew && orderData.client && (
              <p className={`text-xs ${sText}`}>
                {clientContacts.find(c => String(c.id) === String(orderData.client))?.name || `Cliente #${orderData.client}`}
              </p>
            )}
          </div>
          {!isNew && (() => {
            const cfg = STATUS_CONFIG[orderData.status];
            if (!cfg) return null;
            const Icon = cfg.icon;
            return (
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.bg}`}>
                <Icon size={12} />{cfg.label}
              </span>
            );
          })()}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-all"
        >
          <Save size={15} />
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>

      {/* Horizontal tabs */}
      <div className="flex gap-1 px-6 pt-3 border-b border-gray-200">
        {EDITOR_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {activeTab === 'orden'   && TabOrden()}
        {activeTab === 'cliente' && TabCliente()}
        {activeTab === 'totales' && TabTotales()}
        {activeTab === 'detalle' && TabDetalle()}
      </div>
    </div>

    {/* Item Modal */}
    {showItemModal && ItemModal()}

    {/* Delete Item Confirm */}
    {deleteItem && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center border border-gray-200">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle size={24} className="text-red-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2 text-gray-900">Eliminar ítem</h3>
          <p className="text-sm text-gray-500 mb-6">¿Eliminar <strong>{deleteItem.product_name || deleteItem.item_code || 'este ítem'}</strong>?</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => setDeleteItem(null)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">Cancelar</button>
            <button onClick={() => handleDeleteItem(deleteItem.id)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
              <Trash2 size={15} /> Eliminar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const CustomerOrderManager = ({
  theme = 'light',
  contacts = [],
  orderToOpen = null,
  onOrderOpened = null,
}) => {
  const isDark = theme === 'dark';

  const clientContacts = useMemo(() => contacts.filter(c => c.contactType === 'CLIENT'), [contacts]);

  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [activeOrder, setActiveOrder] = useState(null); // null → list, object/NEW → editor
  const [isNew, setIsNew]             = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const [ports, setPorts]       = useState([]);
  const [products, setProducts] = useState([]);

  const sText = isDark ? 'text-[#bdc1c6]' : 'text-gray-600';
  const pText = isDark ? 'text-[#e8eaed]' : 'text-gray-900';
  const cardCls = `rounded-xl border shadow-sm ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`;
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark ? 'bg-[#202124] border-[#3c4043] text-[#e8eaed]' : 'bg-white border-gray-300 text-gray-900'
  }`;

  // Load orders
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await customerOrderService.getAll(params);
      setOrders(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Error cargando pedidos:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Load ports once
  useEffect(() => {
    api.get('/pricing/ports/')
      .then(data => setPorts(Array.isArray(data) ? data : data?.results || []))
      .catch(console.error);
  }, []);

  // Navigate via orderToOpen
  useEffect(() => {
    if (!orderToOpen) return;
    (async () => {
      const detail = await customerOrderService.getById(orderToOpen);
      setActiveOrder(detail);
      setIsNew(false);
      if (onOrderOpened) onOrderOpened();
    })();
  }, [orderToOpen]);

  const openOrder = useCallback(async (orderId) => {
    const detail = await customerOrderService.getById(orderId);
    setActiveOrder(detail);
    setIsNew(false);
  }, []);

  const openNew = useCallback(() => {
    setActiveOrder({});
    setIsNew(true);
  }, []);

  const handleBack = useCallback(() => {
    setActiveOrder(null);
    setIsNew(false);
    loadOrders();
  }, [loadOrders]);

  const handleSaved = useCallback(async (saved) => {
    await loadOrders();
    if (isNew && saved?.id) {
      const detail = await customerOrderService.getById(saved.id);
      setActiveOrder(detail);
      setIsNew(false);
    } else if (saved?.id) {
      const detail = await customerOrderService.getById(saved.id);
      setActiveOrder(detail);
    }
  }, [loadOrders, isNew]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customerOrderService.delete(deleteTarget.id);
      if (activeOrder?.id === deleteTarget.id) setActiveOrder(null);
      setDeleteTarget(null);
      await loadOrders();
    } catch (err) { console.error('Error eliminando pedido:', err); }
    finally { setDeleting(false); }
  }, [deleteTarget, activeOrder, loadOrders]);

  const filteredOrders = useMemo(() => orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return [o.order_code, o.client_name, o.buyer, o.destination_country]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    }
    return true;
  }), [orders, statusFilter, searchTerm]);

  const stats = useMemo(() => ({
    total:    orders.length,
    aprobado: orders.filter(o => o.status === 'aprobado').length,
    totalFob: orders.reduce((s, o) => s + (parseFloat(o.total_fob_price) || 0), 0),
  }), [orders]);

  const renderStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status];
    if (!cfg) return <span className="text-gray-400 text-xs">{status}</span>;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.bg}`}>
        <Icon size={12} />{cfg.label}
      </span>
    );
  };

  // ── Editor view ───────────────────────────────────────────────────────────
  if (activeOrder !== null) {
    return (
      <OrderEditor
        order={isNew ? null : activeOrder}
        contacts={contacts}
        ports={ports}
        products={products}
        theme={theme}
        onBack={handleBack}
        onSaved={handleSaved}
      />
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${pText}`}>Pedidos del Cliente</h1>
          <p className={`text-sm mt-1 ${sText}`}>Gestión de pedidos y órdenes completas</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-500/20 text-sm font-medium"
        >
          <Plus size={18} /> Nuevo Pedido
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Pedidos', value: stats.total,    icon: ClipboardList, color: 'blue' },
          { label: 'Aprobados',     value: stats.aprobado, icon: CheckCircle,   color: 'emerald' },
          { label: 'Total FOB',     value: `$${fmtN(stats.totalFob)}`, icon: DollarSign, color: 'purple' },
        ].map(s => (
          <div key={s.label} className={cardCls + ' p-4'}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-medium uppercase tracking-wider ${sText}`}>{s.label}</span>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${s.color}-100`}>
                <s.icon size={16} className={`text-${s.color}-600`} />
              </div>
            </div>
            <p className={`text-xl font-bold ${pText}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className={cardCls + ' p-4'}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${sText}`} />
            <input
              type="text" placeholder="Buscar por código, cliente, comprador..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className={`${inputCls} pl-9`}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X size={14} className={sText} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Filter size={16} className={sText} />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`${inputCls} w-auto min-w-[220px]`}>
              <option value="all">Todos los estados</option>
              {Object.entries(STATUS_CONFIG).map(([v, cfg]) => (
                <option key={v} value={v}>{cfg.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={cardCls + ' overflow-hidden'}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <ClipboardList size={48} className={`mb-4 ${isDark ? 'text-[#5f6368]' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-semibold mb-1 ${pText}`}>Sin pedidos</h3>
            <p className={`text-sm mb-4 ${sText}`}>
              {searchTerm || statusFilter !== 'all' ? 'Sin resultados para los filtros actuales' : 'Todavía no hay pedidos creados'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <Plus size={16} /> Crear primer pedido
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-[#202124]' : 'bg-gray-50'}>
                  {['Código', 'Estado', 'Cliente', 'Fecha pedido', 'País destino', 'Total piezas', 'Total FOB', 'Margen', 'Acciones'].map(h => (
                    <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${sText}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-[#3c4043]' : 'divide-gray-100'}`}>
                {filteredOrders.map((order, idx) => (
                  <tr
                    key={order.id}
                    onClick={() => openOrder(order.id)}
                    className={`transition-colors cursor-pointer ${
                      idx % 2 === 0
                        ? (isDark ? 'bg-[#292a2d]' : 'bg-white')
                        : (isDark ? 'bg-[#202124]' : 'bg-gray-50/50')
                    } ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-blue-50/30'}`}
                  >
                    <td className={`px-4 py-3 font-semibold ${pText}`}>
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-blue-500" />
                        {order.order_code || `#${order.id}`}
                      </div>
                    </td>
                    <td className="px-4 py-3">{renderStatusBadge(order.status)}</td>
                    <td className={`px-4 py-3 ${pText}`}>{order.client_name || '-'}</td>
                    <td className={`px-4 py-3 ${sText}`}>{fmtDate(order.order_date)}</td>
                    <td className={`px-4 py-3 ${sText}`}>
                      {order.destination_country
                        ? <span className="flex items-center gap-1"><Globe size={13} />{order.destination_country}</span>
                        : '-'}
                    </td>
                    <td className={`px-4 py-3 ${pText}`}>{order.total_pieces != null ? fmtN(order.total_pieces, 0) : '-'}</td>
                    <td className={`px-4 py-3 font-semibold ${pText}`}>{order.total_fob_price != null ? `$${fmtN(order.total_fob_price)}` : '-'}</td>
                    <td className={`px-4 py-3 ${pText}`}>{order.margin != null ? `${fmtN(order.margin)}%` : '-'}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openOrder(order.id)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#3c4043]' : 'hover:bg-gray-100'}`} title="Ver/Editar">
                          <Eye size={15} className="text-blue-500" />
                        </button>
                        <button onClick={() => setDeleteTarget(order)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#3c4043]' : 'hover:bg-gray-100'}`} title="Eliminar">
                          <Trash2 size={15} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && filteredOrders.length > 0 && (
        <p className={`text-xs ${sText}`}>Mostrando {filteredOrders.length} de {orders.length} pedidos</p>
      )}

      {/* Delete order confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-xl shadow-2xl border ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`}>
            <div className="p-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${pText}`}>Confirmar eliminación</h3>
              <p className={`text-sm mb-6 ${sText}`}>
                ¿Eliminar el pedido <strong>{deleteTarget.order_code || deleteTarget.id}</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">Cancelar</button>
                <button onClick={handleDelete} disabled={deleting} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50">
                  <Trash2 size={15} /> {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerOrderManager;
